import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Menu } from 'primereact/menu';
import { ProgressSpinner } from 'primereact/progressspinner';

import Layout from '../Layout/Layout';
import EmptyState from '../SharedComponents/Forms/EmptyState';
import FloatingActionButton from '../SharedComponents/FloatingActionButton/FloatingActionButton';
import DeleteConfirmation from '../SharedComponents/DeleteConfirmation';
import TagControlDialog from '../SharedComponents/TagControlDialog';

import Create from './Components/Create';
import Update from './Components/Update';

import { useTitle, formatDuration } from '../../config/global';
import { useGalleryContext } from '../Contexts/GalleryContext';
import {
  useCompanyPlaylists,
  useDeletePlaylist,
  useGetCompanyTags,
} from '../../queries';
import { toggleDialog, toggleDeleteDialog } from '../../redux/Slices/Global/globalSlice';

import '../Media/css/mediaGallery.css';
import './css/playlistGallery.css';
import { tagDisplayName } from '../../utils/helpers';

/**
 * Playlist Gallery — 2026 redesign matching MediaGallery's visual language.
 * Visual layer only; every react-query hook + Redux dialog flag is reused
 * from the legacy Datatable.jsx. The old grid/list/inline-edit logic is
 * gone; edits open the existing Update dialog.
 */
export default function PlaylistGallery() {
  useTitle('Playlists');
  useGetCompanyTags();

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { layout, setLayout } = useGalleryContext();

  const { openDialog, deleteDialog, companyTags } = useSelector((s) => s.global);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [currentPlaylist, setCurrentPlaylist] = useState({ name: '' });
  const [menuItem, setMenuItem] = useState(null);
  // Sort order is purely client-side here — the company-playlists endpoint
  // doesn't take a sort param, so we re-order in memo below.
  const [sortOrder, setSortOrder] = useState('desc');

  const deleteRow = useDeletePlaylist();

  const scrollRef   = useRef(null);
  const sentinelRef = useRef(null);
  const rowMenuRef  = useRef(null);
  const sortMenuRef = useRef(null);
  const filterMenuRef = useRef(null);
  const overflowMenuRef = useRef(null);

  // ── Infinite scroll ─────────────────────────────────────────────────────
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useCompanyPlaylists({
    per_page: 24,
    search: searchTerm.trim() || undefined,
    tags: selectedTagIds,
  });

  useEffect(() => {
    if (!sentinelRef.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: scrollRef.current ?? null, rootMargin: '600px 0px 400px 0px' },
    );
    io.observe(sentinelRef.current);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const playlists = useMemo(() => {
    const arr = data?.pages?.flatMap((p) => p.items) ?? [];
    // client-side date sort using created_at when present, otherwise the
    // server's natural order is preserved.
    const sorted = [...arr];
    if (sortOrder === 'asc') sorted.reverse();
    return sorted;
  }, [data, sortOrder]);

  // ── Dialog plumbing ────────────────────────────────────────────────────
  const openCreate = () => {
    setCurrentPlaylist({});
    dispatch(toggleDialog('createPlaylist'));
  };
  const openEdit = (item) => {
    setCurrentPlaylist(item);
    dispatch(toggleDialog('updatePlaylist'));
  };
  const openTags = (item) => {
    setCurrentPlaylist(item);
    dispatch(toggleDialog('tagDialog'));
  };
  const confirmDelete = (item) => {
    setCurrentPlaylist(item);
    dispatch(toggleDeleteDialog('playlist'));
  };
  const goToSetup = (id) => navigate(`/playlist/${id}/show`);

  // ── Per-item ⋮ action menu ──────────────────────────────────────────────
  const openRowMenu = (event, item) => {
    setMenuItem(item);
    rowMenuRef.current?.toggle(event);
  };
  const rowMenuModel = useMemo(() => {
    if (!menuItem) return [];
    return [
      { label: t('buttons.play_on_screen', { defaultValue: 'Play on Screen' }), icon: 'pi pi-desktop',  command: () => goToSetup(menuItem.id) },
      { label: t('buttons.edit',           { defaultValue: 'Edit' }),            icon: 'pi pi-pencil',   command: () => openEdit(menuItem) },
      { label: t('buttons.manage_tags',    { defaultValue: 'Manage Tags' }),     icon: 'pi pi-tag',      command: () => openTags(menuItem) },
      { separator: true },
      { label: t('buttons.delete'),                                              icon: 'pi pi-trash',    command: () => confirmDelete(menuItem) },
    ];
  }, [menuItem, t]);

  // ── Sort + Filter (tag) popup models ────────────────────────────────────
  const sortMenuItems = [
    { label: t('media.sort_recent', { defaultValue: 'Recent first' }), icon: sortOrder === 'desc' ? 'pi pi-check' : 'pi pi-circle', command: () => setSortOrder('desc') },
    { label: t('media.sort_old',    { defaultValue: 'Oldest first' }), icon: sortOrder === 'asc'  ? 'pi pi-check' : 'pi pi-circle', command: () => setSortOrder('asc')  },
  ];
  const filterMenuItems = useMemo(() => {
    const items = [
      {
        label: t('playlist.all_tags', { defaultValue: 'All tags' }),
        icon: selectedTagIds.length === 0 ? 'pi pi-check' : 'pi pi-circle',
        command: () => setSelectedTagIds([]),
      },
    ];
    (companyTags || []).forEach((tag) => {
      items.push({
        label: tagDisplayName(tag, t),
        icon: selectedTagIds.includes(tag.id) ? 'pi pi-check' : 'pi pi-circle',
        command: () => setSelectedTagIds((prev) => (
          prev.includes(tag.id) ? prev.filter((x) => x !== tag.id) : [...prev, tag.id]
        )),
      });
    });
    return items;
  }, [companyTags, selectedTagIds, t]);

  const playOnScreen = useCallback((item) => goToSetup(item.id), []);

  // ── MobileBar (≤md) ────────────────────────────────────────────────────
  const MobileBar = (
    <div className="pp-mg-mobile-bar d-md-none">
      <div className="pp-mg-mb-search">
        <i className="pi pi-search" />
        <input
          type="search"
          placeholder={t('media.search_placeholder', { defaultValue: 'Search playlists…' })}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div className="pp-mg-mb-actions">
        <button className="pp-mg-mb-upload" onClick={openCreate}>
          <i className="pi pi-plus" />
          <span className="pp-mg-mb-upload-label">{t('playlist.new', { defaultValue: 'New' })}</span>
        </button>
        <button className="pp-mg-mb-icon" onClick={(e) => sortMenuRef.current?.toggle(e)} aria-haspopup aria-label="Sort"   title={t('media.sort',   { defaultValue: 'Sort' })}>
          <i className="pi pi-sort-alt" />
        </button>
        <button className={`pp-mg-mb-icon ${selectedTagIds.length ? 'is-active' : ''}`} onClick={(e) => filterMenuRef.current?.toggle(e)} aria-haspopup aria-label="Filter" title={t('media.filter', { defaultValue: 'Filter' })}>
          <i className="pi pi-filter" />
        </button>
        <div className="pp-view-toggle pp-mg-mb-view" role="group" aria-label="View mode">
          <button type="button" className={layout === 'grid' ? 'is-active' : ''} onClick={() => setLayout('grid')} title="Grid"><i className="pi pi-th-large" /></button>
          <button type="button" className={layout === 'list' ? 'is-active' : ''} onClick={() => setLayout('list')} title="List"><i className="pi pi-list" /></button>
        </div>
      </div>
      <Menu model={sortMenuItems}   popup ref={sortMenuRef}   appendTo={typeof document !== 'undefined' ? document.body : null} className="pp-mg-mb-popup" />
      <Menu model={filterMenuItems} popup ref={filterMenuRef} appendTo={typeof document !== 'undefined' ? document.body : null} className="pp-mg-mb-popup" />
    </div>
  );

  // ── Desktop bars ───────────────────────────────────────────────────────
  const TopBar = (
    <div className="pp-media-topbar d-none d-md-flex">
      <div className="pp-media-search">
        <i className="pi pi-search" />
        <input
          type="search"
          placeholder={t('playlist.search_placeholder', { defaultValue: 'Search playlists…' })}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div className="pp-media-topbar-actions">
        <button className="pp-media-upload-btn" onClick={openCreate}>
          <i className="pi pi-plus" /> {t('playlist.new_playlist', { defaultValue: 'New Playlist' })}
        </button>
      </div>
    </div>
  );

  const Toolbar = (
    <div className="pp-media-toolbar d-none d-md-flex">
      <button className="pp-media-tool-btn" onClick={(e) => sortMenuRef.current?.toggle(e)}>
        <i className="pi pi-sort-alt" /> {sortOrder === 'desc' ? t('media.sort_recent', { defaultValue: 'Recent first' }) : t('media.sort_old', { defaultValue: 'Oldest first' })}
      </button>
      <button className={`pp-media-tool-btn ${selectedTagIds.length ? 'is-active' : ''}`} onClick={(e) => filterMenuRef.current?.toggle(e)}>
        <i className="pi pi-tag" /> {selectedTagIds.length ? t('playlist.filtered_tags', { defaultValue: '{{n}} tags', n: selectedTagIds.length }) : t('playlist.all_tags', { defaultValue: 'All tags' })}
      </button>
      <div className="pp-media-toolbar-spacer" />
      <div className="pp-view-toggle" role="group" aria-label="View mode">
        <button type="button" className={layout === 'grid' ? 'is-active' : ''} onClick={() => setLayout('grid')} title="Grid view"><i className="pi pi-th-large" /></button>
        <button type="button" className={layout === 'list' ? 'is-active' : ''} onClick={() => setLayout('list')} title="List view"><i className="pi pi-list" /></button>
      </div>
    </div>
  );

  return (
    <Layout padding={false}>
      <div className="pp-media-page">
        {MobileBar}
        {TopBar}
        {Toolbar}

        <div className="pp-media-body pp-fab-safe-bottom" ref={scrollRef}>
          {isLoading && playlists.length === 0 ? (
            <div className="pp-media-loading">
              <ProgressSpinner style={{ width: 48, height: 48 }} strokeWidth="3" />
            </div>
          ) : playlists.length === 0 ? (
            <EmptyState
              icon="pi-list"
              title={t('playlist.empty_title', { defaultValue: 'No playlists yet' })}
              description={t('playlist.empty_desc', { defaultValue: 'Group files into playlists that play on a loop.' })}
              cta={{ icon: 'pi pi-plus', label: t('playlist.new_playlist', { defaultValue: 'New Playlist' }), onClick: openCreate }}
            />
          ) : layout === 'grid' ? (
            <PlaylistGrid items={playlists} onActivate={(it) => goToSetup(it.id)} onMenu={openRowMenu} onPlay={playOnScreen} t={t} />
          ) : (
            <PlaylistList items={playlists} onActivate={(it) => goToSetup(it.id)} onMenu={openRowMenu} onPlay={playOnScreen} t={t} />
          )}
          <div ref={sentinelRef} style={{ height: 1 }} />
        </div>

        {/* Sort/Filter popups for desktop too (sharing same refs) */}
        <Menu model={sortMenuItems}     popup ref={null /* sort already attached to mobile ref */} appendTo={typeof document !== 'undefined' ? document.body : null} className="pp-mg-mb-popup" />
      </div>

      {/* Per-item ⋮ menu */}
      <Menu model={rowMenuModel} popup ref={rowMenuRef} className="pp-media-row-menu" appendTo={typeof document !== 'undefined' ? document.body : null} />
      {/* Overflow menu for mobile (currently empty — placeholder for future bulk ops) */}
      <Menu model={[]} popup ref={overflowMenuRef} appendTo={typeof document !== 'undefined' ? document.body : null} className="pp-mg-mb-popup" />

      {/* Existing dialogs reused as-is */}
      {openDialog?.createPlaylist && <Create />}
      {openDialog?.updatePlaylist && <Update PlayList={currentPlaylist} setReload={refetch} />}
      {openDialog?.tagDialog      && <TagControlDialog PlayList={currentPlaylist} />}
      {deleteDialog?.playlist && (
        <DeleteConfirmation
          deleteHandler={async () => { await deleteRow.mutateAsync({ id: currentPlaylist.id }); refetch(); }}
          itemName={currentPlaylist?.name ?? 'playlist'}
          dialogKey="playlist"
        />
      )}

      <FloatingActionButton
        icon="pi pi-plus"
        label={t('playlist.new_playlist', { defaultValue: 'New Playlist' })}
        onClick={openCreate}
      />
    </Layout>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * GridView
 * ─────────────────────────────────────────────────────────────────── */
function PlaylistGrid({ items, onActivate, onMenu, onPlay, t }) {
  return (
    <div className="pp-media-grid">
      {items.map((item) => <PlaylistGridCard key={item.id} item={item} onActivate={onActivate} onMenu={onMenu} onPlay={onPlay} t={t} />)}
    </div>
  );
}

function PlaylistGridCard({ item, onActivate, onMenu, onPlay, t }) {
  const isInactive = item.has_unpublished_changes === true;
  const itemsCount = item.media_count ?? item.playlist_count ?? null;
  return (
    <div className="pp-media-card pp-playlist-card" onClick={() => onActivate(item)}>
      <div className="pp-media-thumb pp-playlist-thumb">
        <div className="pp-playlist-thumb-bg">
          <i className="pi pi-list" />
        </div>
        <button
          type="button"
          className="pp-media-card-play"
          onClick={(e) => { e.stopPropagation(); onPlay(item); }}
          title={t('buttons.play_on_screen', { defaultValue: 'Play on Screen' })}
          aria-label={t('buttons.play_on_screen', { defaultValue: 'Play on Screen' })}
        ><i className="pi pi-play" /></button>
        <button
          type="button"
          className="pp-media-card-menu"
          onClick={(e) => { e.stopPropagation(); onMenu(e, item); }}
          title="More"
          aria-label="More"
        ><i className="pi pi-ellipsis-v" /></button>
      </div>
      <div className="pp-media-info">
        <div className="pp-media-name" title={item.name}>{item.name}</div>
        <div className="pp-media-meta">
          {isInactive && (
            <span className="pp-pill pp-pill-warning">
              <span className="pp-pill-dot" />
              {t('playlist.draft', { defaultValue: 'Publish required' })}
            </span>
          )}
          {!isInactive && (
            <span className="pp-pill pp-pill-success">
              <span className="pp-pill-dot" />
              {t('playlist.active', { defaultValue: 'Active' })}
            </span>
          )}
          <span>{itemsCount != null ? `${itemsCount} ${t('media.items', { defaultValue: 'items' })} · ` : ''}{formatDuration(item.playlist_duration)}</span>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * ListView
 * ─────────────────────────────────────────────────────────────────── */
function PlaylistList({ items, onActivate, onMenu, onPlay, t }) {
  return (
    <div className="pp-media-list-wrap">
      <table className="pp-media-list">
        <thead>
          <tr>
            <th className="pp-media-col-thumb"></th>
            <th className="pp-media-col-name">{t('media.name', { defaultValue: 'Name' })}</th>
            <th className="pp-media-col-duration d-none d-md-table-cell">{t('media.duration', { defaultValue: 'Duration' })}</th>
            <th className="pp-media-col-type d-none d-md-table-cell">{t('media.status', { defaultValue: 'Status' })}</th>
            <th className="pp-media-col-actions"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => <PlaylistRow key={item.id} item={item} onActivate={onActivate} onMenu={onMenu} onPlay={onPlay} t={t} />)}
        </tbody>
      </table>
    </div>
  );
}

function PlaylistRow({ item, onActivate, onMenu, onPlay, t }) {
  const isInactive = item.has_unpublished_changes === true;
  const itemsCount = item.media_count ?? item.playlist_count ?? null;
  return (
    <tr className="pp-media-row">
      <td className="pp-media-col-thumb">
        <div className="pp-media-list-thumb pp-playlist-list-thumb" onClick={() => onActivate(item)}>
          <i className="pi pi-list" />
        </div>
      </td>
      <td className="pp-media-col-name" onClick={() => onActivate(item)}>
        <span className="pp-media-row-name" title={item.name}>{item.name}</span>
        <span className="pp-media-row-sub d-md-none">
          {itemsCount != null ? `${itemsCount} ${t('media.items', { defaultValue: 'items' })} · ` : ''}{formatDuration(item.playlist_duration)}
        </span>
      </td>
      <td className="pp-media-col-duration d-none d-md-table-cell">
        {formatDuration(item.playlist_duration)}
      </td>
      <td className="pp-media-col-type d-none d-md-table-cell">
        {isInactive
          ? <span className="pp-pill pp-pill-warning"><span className="pp-pill-dot" />{t('playlist.draft', { defaultValue: 'Publish required' })}</span>
          : <span className="pp-pill pp-pill-success"><span className="pp-pill-dot" />{t('playlist.active', { defaultValue: 'Active' })}</span>}
      </td>
      <td className="pp-media-col-actions">
        <div className="pp-media-row-actions">
          <button className="pp-media-row-action pp-media-row-primary" onClick={(e) => { e.stopPropagation(); onPlay(item); }} title={t('buttons.play_on_screen', { defaultValue: 'Play on Screen' })} aria-label={t('buttons.play_on_screen', { defaultValue: 'Play on Screen' })}>
            <i className="pi pi-desktop" />
          </button>
          <button className="pp-media-row-action" onClick={(e) => onMenu(e, item)} title="More" aria-label="More">
            <i className="pi pi-ellipsis-v" />
          </button>
        </div>
      </td>
    </tr>
  );
}
